// Composants de visualisation de la direction A : jauge horizontale avec repère de seuil, cuve à niveau
// (hachures pour une donnée ancienne ou inconnue), ligne de tendance, pastille de qualité de donnée,
// liste d'alertes, échelle d'arrivages. Aucune valeur n'est codée en dur : tout provient des propriétés,
// et une valeur absente s'affiche « indisponible » avec son motif.

import { formaterQuantite, LIBELLES_FRAICHEUR, type Fraicheur, type Unite } from '@carburanttrack/shared';
import { Indisponible } from './Etats';

const borne = (v: number) => Math.max(0, Math.min(100, v));

export interface ProprietesJauge {
  libelle: string;
  sousLibelle?: string;
  /** Part en pourcentage, ou null si inconnue. */
  part: number | null;
  /** Repère de seuil en pourcentage. */
  seuil?: number | null;
  valeur: string;
  sousValeur?: string;
  motifIndisponible?: string;
}

export function Jauge({ libelle, sousLibelle, part, seuil, valeur, sousValeur, motifIndisponible }: ProprietesJauge) {
  return (
    <div className="ligne-jauge">
      <div>
        {libelle}
        {sousLibelle && <small>{sousLibelle}</small>}
      </div>
      <div
        className="jauge"
        role="meter"
        aria-label={libelle}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={part ?? undefined}
        aria-valuetext={part === null ? `indisponible : ${motifIndisponible ?? ''}` : `${Math.round(part)} %`}
      >
        {part !== null && <div className="remplissage" style={{ width: `${borne(part)}%` }} />}
        {seuil !== undefined && seuil !== null && <div className="seuil" style={{ left: `${borne(seuil)}%` }} title={`Seuil ${seuil} %`} />}
      </div>
      <div className="valeur">
        {part === null ? <Indisponible motif={motifIndisponible ?? 'donnée absente'} /> : valeur}
        {sousValeur && <small>{sousValeur}</small>}
      </div>
    </div>
  );
}

export interface ProprietesCuve {
  produit: string;
  capacite: string;
  unite: Unite;
  /** Volume mesuré, ou null si aucune déclaration. */
  volume: string | null;
  fraicheur?: Fraicheur | null;
  age?: string | null;
  motifIndisponible?: string;
}

export function Cuve({ produit, capacite, unite, volume, fraicheur, age, motifIndisponible }: ProprietesCuve) {
  const cap = Number(capacite);
  const part = volume !== null && cap > 0 ? borne((Number(volume) / cap) * 100) : null;
  const classe = volume === null ? 'reservoir inconnue' : fraicheur && fraicheur !== 'recente' ? 'reservoir ancienne' : 'reservoir';
  const description =
    volume === null
      ? `Cuve ${produit}, capacité ${formaterQuantite(capacite, unite)}, niveau indisponible : ${motifIndisponible ?? 'aucune déclaration'}`
      : `Cuve ${produit}, ${formaterQuantite(volume, unite)} sur ${formaterQuantite(capacite, unite)}`;
  return (
    <figure className="cuve">
      <b>{produit}</b>
      <div className={classe} role="img" aria-label={description}>
        {part !== null && <div className="niveau" style={{ height: `${part}%` }} />}
        {volume === null && <span className="mention">indisponible</span>}
      </div>
      {volume === null ? (
        <small>{motifIndisponible ?? 'aucune déclaration'}</small>
      ) : (
        <div className="valeur">{formaterQuantite(volume, unite)}</div>
      )}
      <span className="capacite">{volume === null ? 'capacité ' : 'sur '}{formaterQuantite(capacite, unite)}</span>
      {fraicheur && <PastilleQualite fraicheur={fraicheur} age={age ?? undefined} />}
    </figure>
  );
}

export function Tendance({ valeurs, libelle }: { valeurs: (number | null)[]; libelle: string }) {
  const connues = valeurs.filter((v): v is number => v !== null);
  if (connues.length < 2) return <Indisponible motif="historique insuffisant pour tracer une tendance" />;
  const min = Math.min(...connues);
  const max = Math.max(...connues);
  const etendue = max - min || 1;
  const largeur = 120;
  const pas = largeur / (valeurs.length - 1);
  const segments: string[] = [];
  let courant: string[] = [];
  valeurs.forEach((v, i) => {
    if (v === null) {
      if (courant.length) segments.push(courant.join(' '));
      courant = [];
      return;
    }
    courant.push(`${(i * pas).toFixed(1)},${(26 - ((v - min) / etendue) * 24).toFixed(1)}`);
  });
  if (courant.length) segments.push(courant.join(' '));
  return (
    <svg viewBox={`0 0 ${largeur} 28`} preserveAspectRatio="none" role="img" aria-label={libelle} style={{ width: '100%', height: 28 }}>
      {segments.map((points, i) => (
        <polyline key={i} points={points} fill="none" stroke="var(--primaire-2)" strokeWidth="1.5" />
      ))}
    </svg>
  );
}

export function PastilleQualite({ fraicheur, age }: { fraicheur: Fraicheur | null; age?: string }) {
  if (!fraicheur) return <span className="pastille">aucune déclaration</span>;
  return (
    <span className={`pastille ${fraicheur}`}>
      {LIBELLES_FRAICHEUR[fraicheur]}
      {age ? `, ${age}` : ''}
    </span>
  );
}

export interface AlerteAffichee {
  id: string;
  niveau: 'informative' | 'preventive' | 'elevee' | 'critique';
  horodatage: string;
  titre: string;
  responsable: string;
  etat: string;
}

export function ListeAlertes({ alertes, motifVide }: { alertes: AlerteAffichee[] | null; motifVide: string }) {
  if (alertes === null) return <Indisponible motif={motifVide} />;
  if (alertes.length === 0) return <p className="attenue">{motifVide}</p>;
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {alertes.map((a) => (
        <li key={a.id} className={`alerte ${a.niveau}`}>
          <i aria-hidden="true" />
          <div>
            <div className="meta">
              <span>
                {a.horodatage}, {a.niveau}
              </span>
            </div>
            {a.titre}
            <div className="meta">
              <span>{a.responsable}</span>
              <span>{a.etat}</span>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export interface PalierArrivage {
  horizon: string;
  volume: string | null;
  retard: string | null;
  unite: Unite;
}

export function EchelleArrivages({ paliers, motifIndisponible }: { paliers: PalierArrivage[] | null; motifIndisponible: string }) {
  if (!paliers) return <Indisponible motif={motifIndisponible} />;
  const max = Math.max(...paliers.map((p) => Number(p.volume ?? 0)), 1);
  return (
    <div>
      {paliers.map((p) => {
        const v = Number(p.volume ?? 0);
        const r = Number(p.retard ?? 0);
        return (
          <div key={p.horizon} className="echelle">
            <div>{p.horizon}</div>
            <div className="barre-echelle" aria-hidden="true">
              <i style={{ left: 0, width: `${((v - r) / max) * 100}%` }} />
              {r > 0 && <i className="retard" style={{ left: `${((v - r) / max) * 100}%`, width: `${(r / max) * 100}%` }} />}
            </div>
            <div className="valeur">{p.volume === null ? <Indisponible motif={motifIndisponible} /> : formaterQuantite(p.volume, p.unite)}</div>
          </div>
        );
      })}
    </div>
  );
}
